import { type ReactElement, useEffect, useState } from 'react'

import { logger } from '../../logging/logger'
import type { StripeOnrampClient } from './stripeOnramp'
import { type StripeSdk, loadStripeSdk } from './stripeSdk'
import {
  type StripeOnrampConfig,
  useStripeOnrampClient,
} from './useStripeOnrampClient'

type StripeOnrampBridgeProps = {
  config: StripeOnrampConfig
  onClient: (client: StripeOnrampClient | undefined) => void
}

/**
 * Publishes the Stripe client to `FunkitFlowProvider` without making the SDK a
 * load-time dependency of this package.
 *
 * - Rendered only once a host passed Stripe config, so the optional peer is
 *   fetched only where the binary is expected to carry it.
 * - A failed load is a host misconfiguration, not a user action: it logs at
 *   ERROR with the install steps and leaves the client `undefined`, so every
 *   other rail keeps working instead of the whole app dying at startup.
 */
export function StripeOnrampBridge(
  props: StripeOnrampBridgeProps,
): ReactElement | null {
  const [sdk, setSdk] = useState<StripeSdk | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    loadStripeSdk()
      .then((loaded) => {
        if (cancelled) {
          return
        }
        // The branch that decides whether the Stripe rail exists at all.
        logger.info('connect-rn:stripe_sdk_loaded')
        setSdk(loaded)
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        logger.error('connect-rn:stripe_sdk_unavailable', {
          error: error instanceof Error ? error.message : String(error),
        })
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (sdk === undefined) {
    return null
  }
  return <StripeOnrampClientBinder sdk={sdk} {...props} />
}

/** Split out so the SDK's hooks are only called once the SDK exists. */
function StripeOnrampClientBinder({
  sdk,
  config,
  onClient,
}: StripeOnrampBridgeProps & { sdk: StripeSdk }): null {
  const client = useStripeOnrampClient(sdk, config)
  useEffect(() => {
    onClient(client)
    return () => onClient(undefined)
  }, [client, onClient])
  return null
}
